import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Modal } from 'app/shared/components/modals/Modal';
import { dataGridCol } from '@shared/components/grid/common';
import { formatDate } from '@shared/commonFunctions';
import { dateFormatWithTime } from '@shared/constants/formats';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { ILead } from '../../../components/Leads/types/interfaces/interfaces';

export type ContactLeadsModalPropsType = {
  handleClose: () => void;
  handleSubmit: (data: string[]) => Promise<void> | void;
  leads: ILead[];
  isLoading: boolean;
  ContactLeadsModalPosition: number;
};

const columns: GridColDef[] = [
  dataGridCol('numericId', {
    headerName: 'ID',
    flex: 0.5,
    minWidth: 80,
  }),
  dataGridCol('assetTag', {
    headerName: 'Asset',
    flex: 2,
    minWidth: 120,
  }),
  dataGridCol('primarySourceName', {
    headerName: 'Pri Source',
    flex: 1.5,
    minWidth: 120,
  }),
  dataGridCol('secondarySourceName', {
    headerName: 'Sec Source',
    flex: 1.2,
    minWidth: 120,
  }),
  dataGridCol('noOfNotes', {
    headerName: '# Comms',
    flex: 1,
    minWidth: 80,
  }),
  dataGridCol('lastNoteDateTime', {
    headerName: 'Last Comm',
    flex: 1.5,
    minWidth: 120,
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
  }),
  dataGridCol('currentWorkFlowStepName', {
    headerName: 'Workflow Step ',
    flex: 1.2,
    minWidth: 120,
  }),
  dataGridCol('currentWorkFlowStepDate', {
    headerName: 'Step Date',
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    flex: 1.5,
    minWidth: 105,
  }),
  dataGridCol('assignedUser', {
	headerName: 'User',
	valueGetter: ({ value }) => value?.name,
	minWidth: 90,
	flex: 2,
}),
  dataGridCol('lastActivity'),
  dataGridCol('lastActivityDate', {
    headerName: 'Activity Date',
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    flex: 1.5,
    minWidth: 105,
  }),
];

export const ContactLeadsModal = ({ handleClose, handleSubmit, leads = [], isLoading, ContactLeadsModalPosition }: ContactLeadsModalPropsType) => {
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);

  const sortedLeads = useMemo(() => {
    const sortedLeads = [...leads]?.sort((a, b) => b.createdDate.localeCompare(a.createdDate));

    return sortedLeads;
  }, [leads]);
    
  return (
    <Modal
      open
      hideBackdrop
      title="Contact Leads"
      dialogWidth="1300px"
      resetPosition={ContactLeadsModalPosition}
      onClose={handleClose}
    >
      <>
        <Box>
          <PhoenixGrid
            autoHeight
            withHeader
            canNotCopy
            noButton
            checkboxSelection
            className="leads"
            columns={columns}
            rows={sortedLeads}
            loading={!leads}
            pageSize={5}
            onSelectionModelChange={(value) => {
              setSelectedLeads(value as string[]);
            }}
          />
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
        >
          <CancelButton onClick={handleClose}/>
          <SaveButton
            label="Transfer"
            isLoading={isLoading}
            disabled={selectedLeads?.length <= 0}
            onClick={() => handleSubmit?.(selectedLeads)}
          />
        </Box>
      </>
    </Modal>
  );
};
